import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'

interface User {
  id: string
  email: string
  full_name: string
  phone?: string
  user_type: string
  role?: string
  credits?: number
  avatar?: string
}

interface AuthState {
  user: User | null
  token: string
  refreshToken: string
  isAuthenticated: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string>('')
  const refreshToken = ref<string>('')
  const isAuthenticated = ref<boolean>(false)
  const isLoading = ref<boolean>(false)

  const isLoggedIn = computed(() => isAuthenticated.value && !!token.value)

  function init() {
    // Load from localStorage
    const stored = localStorage.getItem('auth')
    if (stored) {
      try {
        const data = JSON.parse(stored)
        user.value = data.user
        token.value = data.token
        refreshToken.value = data.refreshToken
        isAuthenticated.value = data.isAuthenticated
      } catch (e) {
        console.error('Failed to parse auth from localStorage:', e)
      }
    }
  }

  function setAuth(data: AuthState) {
    user.value = data.user
    token.value = data.token
    refreshToken.value = data.refreshToken
    isAuthenticated.value = data.isAuthenticated
    saveToStorage()
  }

  function setUser(userData: User) {
    user.value = userData
    saveToStorage()
  }

  function setTokens(tokens: { token: string; refreshToken: string }) {
    token.value = tokens.token
    refreshToken.value = tokens.refreshToken
    isAuthenticated.value = true
    saveToStorage()
  }

  async function login(credentials: { email: string; password: string }) {
    isLoading.value = true
    try {
      const response = await apiService.post('/api/v1/auth/login', credentials)
      console.log('📥 Login raw response:', JSON.stringify(response.data))
      // Backend wraps in {success, data: {token, user, staff, ...}}
      const body = response.data || response
      console.log('📥 body (response.data):', JSON.stringify(body))
      const data = body.data || body
      console.log('📥 data (body.data || body):', JSON.stringify(data))
      console.log('📥 token:', data.token, '| user:', data.user?.email, '| tenant_id:', data.user?.tenant_id)
      setAuth({
        user: data.user,
        token: data.token,
        refreshToken: '',
        isAuthenticated: true
      })
      // Verify token was stored
      const stored = localStorage.getItem('auth')
      console.log('📥 Stored auth token:', stored ? JSON.parse(stored).token?.substring(0, 20) + '...' : 'NULL')
      // Save tenant_id to localStorage for HR/accounting services
      if (data.user?.tenant_id) {
        localStorage.setItem('tenantId', data.user.tenant_id)
      }
      // Save firm_id to localStorage for office management
      if (data.staff?.firm_id) {
        localStorage.setItem('firmId', data.staff.firm_id)
      } else if (data.firm?.firm_id) {
        localStorage.setItem('firmId', data.firm.firm_id)
      }
      return data
    } finally {
      isLoading.value = false
    }
  }

  /** Get dashboard route based on role or user_type (role takes priority) */
  function getDashboardRoute(): string {
    // First check role from roles table (set on staff records)
    const role = user.value?.role
    if (role) {
      switch (role) {
        case 'owner':
        case 'partner':
        case 'firm_company':
        case 'staff':
          return '/dashboard/office'
        case 'firm_accountant':
        case 'financial_manager':
          return '/dashboard/accountant'
        case 'hr_manager':
          return '/dashboard/hr'
        case 'lawyer':
          return '/dashboard/lawyer'
        case 'client':
        case 'user':
          return '/dashboard/client'
        case 'collection_agent':
          return '/dashboard/collection'
        case 'installment_officer':
          return '/dashboard/installment'
        case 'super_admin':
          return '/admin'
      }
    }
    // Fallback to user_type
    const userType = user.value?.user_type
    switch (userType) {
      case 'lawyer':
        return '/dashboard/lawyer'
      case 'client':
      case 'customer':
        return '/dashboard/client'
      case 'firm_admin':
      case 'staff':
        return '/dashboard/office'
      case 'firm_accountant':
      case 'accountant':
        return '/dashboard/accountant'
      case 'collection_agent':
      case 'collection_manager':
        return '/dashboard/collection'
      case 'installment_officer':
      case 'installment_manager':
        return '/dashboard/installment'
      case 'hr_manager':
      case 'hr_officer':
        return '/dashboard/hr'
      case 'admin':
        return '/admin'
      default:
        return '/'
    }
  }

  async function logout() {
    user.value = null
    token.value = ''
    refreshToken.value = ''
    isAuthenticated.value = false
    localStorage.removeItem('auth')
    localStorage.removeItem('tenantId')
    localStorage.removeItem('firmId')
  }

  function saveToStorage() {
    localStorage.setItem('auth', JSON.stringify({
      user: user.value,
      token: token.value,
      refreshToken: refreshToken.value,
      isAuthenticated: isAuthenticated.value
    }))
  }

  return {
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoggedIn,
    isLoading,
    init,
    login,
    setAuth,
    setUser,
    setTokens,
    logout,
    getDashboardRoute
  }
})
